import { autoinject, BindingEngine, Disposable, computedFrom, useView } from "aurelia-framework";
import {
    GhgVerificationClientModel, GhgVerificationStatusEnum,
    GhgVerificationEnergySavingEquipmentModel, GhgVerificationRatingFactorClientModel,
    GhgVerificationStatusHistoryClientModel
} from "../../../../../../models/ghgs/ghg.models";
import { GhgService } from "../../../../../../services/ghg/ghg.service";
import { UserClientService } from "../../../../../../services/authentication/user-client.service";
import { GhgFactorsRenderer } from "../../helpers/ghg-factors-renderer";
import { GhgVerification } from "../../ghg";
import { RightShipQiRouterConstants } from "../../../../../../configuration/rightshipqi-router.constants";
import { Reflection } from "../../../../../../utilities/reflection";
import { Router } from "aurelia-router";

@autoinject()
@useView('views/pages/modules/ghg/steps/ghg-overview/ghg-overview.html')
export class GhgOverview {

    ghgVerification: GhgVerificationClientModel;
    reassignment: GhgVerificationStatusEnum = GhgVerificationStatusEnum.GhgVerificationReassignment;
    passedGhgVerificationRatingFactors: GhgVerificationRatingFactorClientModel[] = [];
    failedGhgVerificationRatingFactors: GhgVerificationRatingFactorClientModel[] = [];
    naGhgVerificationRatingFactors: GhgVerificationRatingFactorClientModel[] = [];
    passedGhgVerificationEnergySavingEquipments: GhgVerificationEnergySavingEquipmentModel[];
    failedGhgVerificationEnergySavingEquipments: GhgVerificationEnergySavingEquipmentModel[];
    naGhgVerificationEnergySavingEquipments: GhgVerificationEnergySavingEquipmentModel[];
    currentRatingsSummary: any;
    ghgVerificationRatingFactors: GhgVerificationRatingFactorClientModel[] = [];
    ghgVerificationEnergySavingEquipments: GhgVerificationEnergySavingEquipmentModel[];
    subscriptions: Disposable[] = [];
    lastGhgStatusHistory: GhgVerificationStatusHistoryClientModel;
    customerId: number;
    ghgFactorsRenderer: GhgFactorsRenderer;

    @computedFrom("ghgVerification.ghgVerificationStatusId")

    get shouldShowGhgRatingAfterAcceptance() {
        return (this.ghgVerification.ghgVerificationStatusId == GhgVerificationStatusEnum.Accepted && !this.ghgVerification.havingOnlyCiiFactors) ||
            this.ghgVerification.ghgVerificationStatusId == GhgVerificationStatusEnum.Rejected
    }

    constructor(private ghgService: GhgService, private bindingEngine: BindingEngine,
        userClientService: UserClientService, private router: Router) {
        this.customerId = userClientService.currentUser.settings.customerId;
    }

    activate(model: { ghgVerification: GhgVerificationClientModel }) {

        if (!model) {
            return;
        }

        this.subscriptions.forEach(sub => sub.dispose());
        this.subscriptions = [];

        if (model.ghgVerification) {
            this.ghgVerification = model.ghgVerification;
            this.subscriptions.push(this.bindingEngine.propertyObserver(this.ghgVerification, 'isClosed').subscribe(() => {
                this.loadOutcomeData();
            }));
            if (this.ghgVerification.isClosed) {
                this.loadOutcomeData();
            }
        }
    }

    detached() {
        this.subscriptions.forEach(sub => sub.dispose());
        this.subscriptions = [];
    }

    public loadOutcomeData() {

        this.ghgService.getGhgVerificationStatusClientModel(this.ghgVerification.ghgVerificationId).then(response => {

            this.ghgVerificationRatingFactors = response.ghgVerificationRatingFactors;
            this.ghgVerificationEnergySavingEquipments = response.ghgVerificationEnergySavingEquipments;

            this.currentRatingsSummary = response.revisedRatingsSummary;

            this.lastGhgStatusHistory = this.ghgVerification.ghgVerificationStatusHistory[0];

            this.ghgFactorsRenderer = new GhgFactorsRenderer(this.ghgVerificationRatingFactors, this.ghgVerification);

            this.ghgVerificationRatingFactors.forEach(factor => {
                factor.isVisible = this.ghgFactorsRenderer.showFactor(factor);
            });

            this.passedGhgVerificationRatingFactors = this.ghgVerificationRatingFactors.filter(x => x.passed === true && x.isVisible);
            this.failedGhgVerificationRatingFactors = this.ghgVerificationRatingFactors.filter(x => x.passed === false && x.isVisible);
            this.naGhgVerificationRatingFactors = this.ghgVerificationRatingFactors.filter(x => x.passed === null && x.isVisible);

            this.passedGhgVerificationEnergySavingEquipments = this.ghgVerificationEnergySavingEquipments.filter(x => x.passed === true);
            this.failedGhgVerificationEnergySavingEquipments = this.ghgVerificationEnergySavingEquipments.filter(x => x.passed === false);
            this.naGhgVerificationEnergySavingEquipments = this.ghgVerificationEnergySavingEquipments.filter(x => x.passed === null);
        });
    }

    public filtersHtml(factor: GhgVerificationRatingFactorClientModel, isCurrent: boolean, isEditable: boolean): string {

        const propValue = isCurrent ? "previousValueBeforeOutcome" : (factor.revisedValue !== null ? "revisedValue" : "submittedValue");

        return this.ghgFactorsRenderer.filtersHtml(factor, propValue, isEditable);
    }

    @computedFrom(Reflection.getPropertyPath<GhgVerification>(p => p.ghgVerification.vesselId))
    public get vesselUrl() {
        return this.router.generate(RightShipQiRouterConstants.RouteNames.VesselDetails, {
            id: this.ghgVerification.vesselId,
        });
    }

    @computedFrom(Reflection.getPropertyPath<GhgVerification>(p => p.ghgVerification.ghgVerificationStatusId))
    public get cancelStatus() {
        return this.ghgVerification.ghgVerificationStatusId == GhgVerificationStatusEnum.Cancelled || this.ghgVerification.ghgVerificationStatusId == GhgVerificationStatusEnum.Rejected;
    }
}